/**
 * @fileoverview Ultra-responsive, touch-optimized mobile fan view for Buzzer Battle ("De barrio en barrio") (/buzzer).
 * Implements Single Shared Device Architecture (1 physical mobile device in arena shared between contestants),
 * dynamic identity role adoption (name, theme color, avatar of active turn player J1/J2), real-time bidirectional sync,
 * touch-optimized large targets (>60px), rebound disabled/struck-through option states, verdict reveal banners,
 * podium view, Screen WakeLock management, and tactical haptic vibration cues.
 * 
 * @module components/Dashboard/Sections/BuzzerBattleFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ConnectWebSocketForBuzzerBattleFan,
    DisconnectWebSocketForBuzzerBattleFan,
    SendBuzzerBattleOptionSelection,
    RequestBuzzerBattleFanState
} from '../../../utilities/WS/BuzzerBattleFanWS';
import {
    GetRealtimeState,
    GetTheme,
    CalculatePodium
} from '../../../utilities/BL/BuzzerBattle';

/**
 * Option letter prefixes for multiple-choice questions.
 * @constant {Array<string>}
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Default fallback player configurations.
 * @constant {Array<Object>}
 */
const DEFAULT_PLAYERS_SETUP = [
    {
        id: 'J1',
        playerIndex: 0,
        name: 'Jugador 1',
        color: '#00e5ff',
        avatarUrl: '',
        keyCode: 'KeyA',
        score: 0
    },
    {
        id: 'J2',
        playerIndex: 1,
        name: 'Jugador 2',
        color: '#ff0055',
        avatarUrl: '',
        keyCode: 'KeyL',
        score: 0
    }
];

/**
 * Default theme color fallbacks.
 * @constant {Object}
 */
const DEFAULT_THEME_CONFIGURATION = {
    fanBackgroundType: 'color',
    fanBackgroundColor: '#0a0a16',
    fanBackgroundUrl: '',
    fanTextColor: '#ffffff',
    player1Color: '#00e5ff',
    player2Color: '#ff0055',
    brandLogoUrl: '',
    coverTitle: 'BUZZER BATTLE'
};

/**
 * BuzzerBattleFan Component
 * 
 * @function BuzzerBattleFan
 * @returns {React.ReactElement} The rendered mobile fan interface.
 */
export const BuzzerBattleFan = () => {
    // =========================================================================
    // 1. ESTADO DE CONECTIVIDAD Y TEMA DINÁMICO
    // =========================================================================
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('connecting');
    const [activeThemeData, setActiveThemeData] = useState(null);

    // =========================================================================
    // 2. ESTADO DEL MOTOR DE JUEGO EN TIEMPO REAL
    // =========================================================================
    const [gameState, setGameState] = useState({
        state: 'idle', // idle, cover, game_ready, question_active, turn_active, option_selected, revealed, finished
        themeId: null,
        questionSetId: null,
        themeName: '',
        questionSetTitle: '',
        currentQuestionIndex: 0,
        totalQuestions: 0,
        currentQuestion: null,
        activeTurnPlayerId: null,
        selectedOptionId: null,
        selectedOptionText: '',
        disabledOptionIds: [],
        players: DEFAULT_PLAYERS_SETUP,
        showOptions: true,
        isAnswerRevealed: false,
        isCorrect: null,
        winner: null,
        finalScoresJson: null
    });

    // Referencias mutables para gestión de WakeLock y Haptics
    const screenWakeLockSentinelRef = useRef(null);
    const lastActiveTurnPlayerRef = useRef(null);

    // =========================================================================
    // 3. HELPERS DE VIBRACIÓN HÁPTICA Y FORMATO MULTIMEDIA
    // =========================================================================

    /**
     * Executes safe haptic feedback vibration on supporting mobile devices.
     * 
     * @function triggerHapticFeedback
     * @param {number|Array<number>} vibrationPattern - Milliseconds or pattern array.
     */
    const triggerHapticFeedback = useCallback((vibrationPattern) => {
        if (typeof window !== 'undefined' && 'navigator' in window && 'vibrate' in navigator) {
            try {
                navigator.vibrate(vibrationPattern);
            } catch (vibrationError) {
                // Ignore restriction on background tabs
            }
        }
    }, []);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media URL.
     * @returns {string} Absolute media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    // =========================================================================
    // 4. GESTIÓN DEL SCREEN WAKELOCK (MANTENER PANTALLA ACTIVA)
    // =========================================================================

    /**
     * Requests screen WakeLock so the mobile screen stays on in arena.
     * 
     * @async
     * @function requestScreenWakeLock
     */
    const requestScreenWakeLock = useCallback(async () => {
        try {
            if (typeof window !== 'undefined' && 'wakeLock' in navigator && !screenWakeLockSentinelRef.current) {
                const wakeLock = await navigator.wakeLock.request('screen');
                screenWakeLockSentinelRef.current = wakeLock;

                wakeLock.addEventListener('release', () => {
                    screenWakeLockSentinelRef.current = null;
                });
            }
        } catch (wakeLockError) {
            // Non-blocking fallback
        }
    }, []);

    /**
     * Releases active screen WakeLock cleanly.
     * 
     * @async
     * @function releaseScreenWakeLock
     */
    const releaseScreenWakeLock = useCallback(async () => {
        try {
            if (screenWakeLockSentinelRef.current) {
                await screenWakeLockSentinelRef.current.release();
                screenWakeLockSentinelRef.current = null;
            }
        } catch (releaseError) {
            screenWakeLockSentinelRef.current = null;
        }
    }, []);

    // Handle visibility changes to retain WakeLock
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestScreenWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        requestScreenWakeLock();

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseScreenWakeLock();
        };
    }, [requestScreenWakeLock, releaseScreenWakeLock]);

    // =========================================================================
    // 5. CARGA INICIAL DE ESTADO Y TEMA
    // =========================================================================
    useEffect(() => {
        const fetchInitialStateAndTheme = async () => {
            try {
                const realtimeResponse = await GetRealtimeState();
                if (realtimeResponse.success && realtimeResponse.data) {
                    const data = realtimeResponse.data;
                    if (data.currentTheme) {
                        setActiveThemeData(data.currentTheme);
                    } else if (data.themeId) {
                        const themeResponse = await GetTheme(data.themeId);
                        if (themeResponse.success && themeResponse.data) {
                            setActiveThemeData(themeResponse.data);
                        }
                    }

                    setGameState((prev) => ({
                        ...prev,
                        ...data,
                        players: (data.players && data.players.length > 0) ? data.players : prev.players
                    }));
                }
            } catch (fetchError) {
                // Non-blocking fallback
            }
        };

        fetchInitialStateAndTheme();
    }, []);

    // =========================================================================
    // 6. PROCESAMIENTO DE MENSAJES WEBSOCKET
    // =========================================================================

    /**
     * Handles incoming real-time WebSocket packets.
     * 
     * @function handleIncomingWebSocketMessage
     * @param {Object} messagePacket - Parsed packet from server.
     */
    const handleIncomingWebSocketMessage = useCallback((messagePacket) => {
        if (!messagePacket || !messagePacket.type) return;

        const normalizedType = messagePacket.type.toLowerCase();
        const incomingState = messagePacket.state || messagePacket.data || messagePacket.payload || {};

        if (incomingState.currentTheme) {
            setActiveThemeData(incomingState.currentTheme);
        }

        switch (normalizedType) {
            case 'buzzer_snapshot':
            case 'buzzer_state': {
                if (incomingState && typeof incomingState === 'object') {
                    setGameState((prev) => ({
                        ...prev,
                        ...incomingState,
                        players: (incomingState.players && incomingState.players.length > 0) ? incomingState.players : prev.players
                    }));
                }
                break;
            }

            case 'buzzer_cover_shown':
            case 'buzzer_show_cover': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'cover',
                    activeTurnPlayerId: null,
                    selectedOptionId: null,
                    isAnswerRevealed: false
                }));
                break;
            }

            case 'buzzer_game_shown':
            case 'buzzer_show_game':
            case 'buzzer_match_initialized': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'game_ready',
                    activeTurnPlayerId: null,
                    selectedOptionId: null,
                    disabledOptionIds: [],
                    isAnswerRevealed: false,
                    isCorrect: null,
                    winner: null
                }));
                break;
            }

            case 'buzzer_question_launched':
            case 'buzzer_question_intro': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'question_active',
                    activeTurnPlayerId: null,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    disabledOptionIds: [],
                    isAnswerRevealed: false,
                    isCorrect: null
                }));
                break;
            }

            case 'buzzer_options_shown': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    showOptions: true
                }));
                break;
            }

            case 'buzzer_turn_assigned': {
                const assignedPlayerId = messagePacket.playerId || incomingState.activeTurnPlayerId || incomingState.playerId;
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    activeTurnPlayerId: assignedPlayerId,
                    state: 'turn_active',
                    isAnswerRevealed: false,
                    isCorrect: null
                }));
                // Haptic vibration signaling active turn unlocked
                triggerHapticFeedback([50, 40, 70]);
                break;
            }

            case 'buzzer_option_selected':
            case 'buzzer_telemetry_selection': {
                const optionId = messagePacket.data?.optionId ?? messagePacket.optionId ?? incomingState.selectedOptionId;
                const optionText = messagePacket.data?.optionText ?? messagePacket.optionText ?? incomingState.selectedOptionText;
                const turnPlayerId = messagePacket.data?.playerId ?? messagePacket.playerId ?? incomingState.activeTurnPlayerId;

                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    selectedOptionId: optionId,
                    selectedOptionText: optionText,
                    activeTurnPlayerId: turnPlayerId || prev.activeTurnPlayerId,
                    state: 'option_selected'
                }));
                triggerHapticFeedback(25);
                break;
            }

            case 'buzzer_option_cleared': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    state: 'turn_active'
                }));
                triggerHapticFeedback(30);
                break;
            }

            case 'buzzer_result_revealed':
            case 'buzzer_force_updated': {
                const isCorrectResult = messagePacket.isCorrect !== undefined ? messagePacket.isCorrect : incomingState.isCorrect;
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'revealed',
                    isAnswerRevealed: true,
                    isCorrect: isCorrectResult
                }));

                if (isCorrectResult) {
                    triggerHapticFeedback([80, 50, 80, 50, 160]);
                } else {
                    triggerHapticFeedback([200, 80, 200]);
                }
                break;
            }

            case 'buzzer_rebound':
            case 'buzzer_rebound_granted': {
                const reboundPlayerId = messagePacket.nextPlayerId || incomingState.activeTurnPlayerId || incomingState.nextPlayerId;
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    activeTurnPlayerId: reboundPlayerId,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    isAnswerRevealed: false,
                    isCorrect: null,
                    state: 'turn_active'
                }));
                // Haptic vibration signaling rebound
                triggerHapticFeedback([60, 40, 60, 40, 100]);
                break;
            }

            case 'buzzer_next_question': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    activeTurnPlayerId: null,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    disabledOptionIds: [],
                    isAnswerRevealed: false,
                    isCorrect: null,
                    state: 'question_active'
                }));
                break;
            }

            case 'buzzer_final_podium':
            case 'buzzer_game_over':
            case 'buzzer_final_results': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'finished',
                    winner: messagePacket.winner || incomingState.winner
                }));
                triggerHapticFeedback([100, 60, 100, 60, 250]);
                break;
            }

            case 'buzzer_reset':
            case 'buzzer_match_reset': {
                setGameState((prev) => ({
                    ...prev,
                    ...incomingState,
                    state: 'idle',
                    currentQuestionIndex: 0,
                    currentQuestion: null,
                    activeTurnPlayerId: null,
                    selectedOptionId: null,
                    selectedOptionText: '',
                    disabledOptionIds: [],
                    isAnswerRevealed: false,
                    isCorrect: null,
                    winner: null
                }));
                break;
            }

            default:
                break;
        }
    }, [triggerHapticFeedback]);

    // WebSocket lifecycle connection effect
    useEffect(() => {
        ConnectWebSocketForBuzzerBattleFan(
            handleIncomingWebSocketMessage,
            (newStatus) => setWebSocketConnectionStatus(newStatus)
        );

        // Request initial state snapshot
        RequestBuzzerBattleFanState();

        return () => {
            DisconnectWebSocketForBuzzerBattleFan();
        };
    }, [handleIncomingWebSocketMessage]);

    // =========================================================================
    // 7. RESOLUCIÓN DE IDENTIDAD DINÁMICA DEL JUGADOR CON TURNO ACTIVO
    // =========================================================================
    const player1Config = useMemo(() => {
        const p1 = gameState.players?.[0] || DEFAULT_PLAYERS_SETUP[0];
        return {
            ...p1,
            color: p1.color || activeThemeData?.player1Color || DEFAULT_THEME_CONFIGURATION.player1Color,
            avatarUrl: p1.avatar || p1.avatarUrl || activeThemeData?.player1Avatar || ''
        };
    }, [gameState.players, activeThemeData]);

    const player2Config = useMemo(() => {
        const p2 = gameState.players?.[1] || DEFAULT_PLAYERS_SETUP[1];
        return {
            ...p2,
            color: p2.color || activeThemeData?.player2Color || DEFAULT_THEME_CONFIGURATION.player2Color,
            avatarUrl: p2.avatar || p2.avatarUrl || activeThemeData?.player2Avatar || ''
        };
    }, [gameState.players, activeThemeData]);

    const activeTurnPlayer = useMemo(() => {
        if (!gameState.activeTurnPlayerId) return null;
        const normalizedId = String(gameState.activeTurnPlayerId).trim().toLowerCase();

        if (normalizedId === 'j1' || normalizedId === 'keya' || normalizedId === 'player_0' || normalizedId === String(player1Config.id).toLowerCase()) {
            return player1Config;
        }
        if (normalizedId === 'j2' || normalizedId === 'keyl' || normalizedId === 'player_1' || normalizedId === String(player2Config.id).toLowerCase()) {
            return player2Config;
        }

        // Search custom players
        const matched = gameState.players?.find(
            (p) => String(p.id).toLowerCase() === normalizedId || String(p.keyCode).toLowerCase() === normalizedId
        );
        if (matched) {
            return {
                ...matched,
                color: matched.color || '#00e5ff',
                avatarUrl: matched.avatar || matched.avatarUrl || ''
            };
        }
        return player1Config;
    }, [gameState.activeTurnPlayerId, gameState.players, player1Config, player2Config]);

    // Track active turn transition for haptic cue
    useEffect(() => {
        if (activeTurnPlayer && activeTurnPlayer.id !== lastActiveTurnPlayerRef.current) {
            lastActiveTurnPlayerRef.current = activeTurnPlayer.id;
        } else if (!activeTurnPlayer) {
            lastActiveTurnPlayerRef.current = null;
        }
    }, [activeTurnPlayer]);

    // =========================================================================
    // 8. MANEJADOR DE SELECCIÓN DE RESPUESTA
    // =========================================================================

    /**
     * Handles tapping an option button on the mobile touchscreen.
     * 
     * @function handleSelectOption
     * @param {Object} optionItem - Selected option data.
     */
    const handleSelectOption = (optionItem) => {
        if (!optionItem || !optionItem.id) return;

        // Check if options are locked or revealed
        if (gameState.isAnswerRevealed) return;

        // Check if option is disabled due to previous strike in rebound
        if (gameState.disabledOptionIds?.includes(optionItem.id)) return;

        // Optimistic local update & instant haptic feedback
        triggerHapticFeedback(20);
        setGameState((prev) => ({
            ...prev,
            selectedOptionId: optionItem.id,
            selectedOptionText: optionItem.text || ''
        }));

        // Dispatch selection via WebSocket
        SendBuzzerBattleOptionSelection(optionItem.id);
    };

    // =========================================================================
    // 9. RENDERIZADO DEL FONDO DINÁMICO
    // =========================================================================
    const renderDynamicBackground = () => {
        const backgroundType = activeThemeData?.fanBackgroundType || DEFAULT_THEME_CONFIGURATION.fanBackgroundType;
        const backgroundColor = activeThemeData?.fanBackgroundColor || DEFAULT_THEME_CONFIGURATION.fanBackgroundColor;
        const backgroundMediaUrl = activeThemeData?.fanBackgroundUrl ? resolveMediaUrl(activeThemeData.fanBackgroundUrl) : '';

        if (backgroundType === 'video' && backgroundMediaUrl) {
            return (
                <div className="buzzer-fan__background">
                    <video
                        src={backgroundMediaUrl}
                        className="buzzer-fan__background--video"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                    <div className="buzzer-fan__background--overlay" />
                </div>
            );
        }

        if (backgroundType === 'image' && backgroundMediaUrl) {
            return (
                <div className="buzzer-fan__background">
                    <img
                        src={backgroundMediaUrl}
                        className="buzzer-fan__background--image"
                        alt="Fondo Buzzer Battle"
                    />
                    <div className="buzzer-fan__background--overlay" />
                </div>
            );
        }

        return (
            <div className="buzzer-fan__background">
                <div
                    className="buzzer-fan__background--color"
                    style={{ backgroundColor }}
                />
                <div className="buzzer-fan__background--overlay" />
            </div>
        );
    };

    // =========================================================================
    // 10. DETERMINACIÓN DEL ESTADO DE VISTA
    // =========================================================================
    const isFinishedState = gameState.state === 'finished';
    const isQuestionActive = gameState.currentQuestion !== null && gameState.state !== 'idle' && gameState.state !== 'cover' && !isFinishedState;
    const currentQuestionNumber = (gameState.currentQuestionIndex || 0) + 1;
    const totalQuestionsCount = gameState.totalQuestions || 10;
    const activePlayerPrimaryColor = activeTurnPlayer?.color || '#00e5ff';

    // Podium calculations if finished
    const podiumStandings = useMemo(() => {
        if (!isFinishedState) return null;
        return CalculatePodium(gameState.players, gameState.finalScoresJson);
    }, [isFinishedState, gameState.players, gameState.finalScoresJson]);

    // =========================================================================
    // 11. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <div
            className={`buzzer-fan ${activeTurnPlayer ? 'buzzer-fan--active-turn' : ''}`}
            style={{
                '--player-color': activePlayerPrimaryColor
            }}
        >
            {/* Capa de fondo dinámico */}
            {renderDynamicBackground()}

            {/* Cabecera Superior con Branding y Estado de Red */}
            <header className="buzzer-fan__header">
                <div className="buzzer-fan__header__logo-container">
                    {activeThemeData?.brandLogoUrl ? (
                        <img
                            src={resolveMediaUrl(activeThemeData.brandLogoUrl)}
                            alt="Logo Marca"
                            className="buzzer-fan__header__logo"
                        />
                    ) : (
                        <img
                            src="/img/page/adsl-five.png"
                            alt="Logo Dorados FC"
                            className="buzzer-fan__header__logo"
                        />
                    )}
                    <h1 className="buzzer-fan__header__dynamic-title">
                        {activeThemeData?.coverTitle || 'BUZZER BATTLE'}
                    </h1>
                </div>

                <div
                    className={`buzzer-fan__header__status-badge buzzer-fan__header__status-badge--${webSocketConnectionStatus}`}
                    title={`Estado de conexión: ${webSocketConnectionStatus}`}
                >
                    <span className="buzzer-fan__header__status-dot" />
                    <span>{webSocketConnectionStatus === 'connected' ? 'En Vivo' : webSocketConnectionStatus}</span>
                </div>
            </header>

            {/* Mini Scoreboard Superior (Jugador 1 vs Jugador 2) */}
            <section className="buzzer-fan__scoreboard">
                {/* Jugador 1 */}
                <div
                    className={`buzzer-fan__scoreboard__player buzzer-fan__scoreboard__player--p1 ${
                        activeTurnPlayer?.id === player1Config.id ? 'buzzer-fan__scoreboard__player--active' : ''
                    }`}
                >
                    {player1Config.avatarUrl ? (
                        <img
                            src={resolveMediaUrl(player1Config.avatarUrl)}
                            alt={player1Config.name}
                            className="buzzer-fan__scoreboard__avatar"
                            style={{ borderColor: player1Config.color }}
                        />
                    ) : (
                        <div
                            className="buzzer-fan__scoreboard__avatar buzzer-fan__scoreboard__avatar--placeholder"
                            style={{ backgroundColor: player1Config.color }}
                        >
                            <i className="bi bi-person-fill" />
                        </div>
                    )}
                    <div className="buzzer-fan__scoreboard__info">
                        <span className="buzzer-fan__scoreboard__name" style={{ color: player1Config.color }}>
                            {player1Config.name}
                        </span>
                        <span className="buzzer-fan__scoreboard__score">{player1Config.score ?? 0} pts</span>
                    </div>
                </div>

                <div className="buzzer-fan__scoreboard__vs">VS</div>

                {/* Jugador 2 */}
                <div
                    className={`buzzer-fan__scoreboard__player buzzer-fan__scoreboard__player--p2 ${
                        activeTurnPlayer?.id === player2Config.id ? 'buzzer-fan__scoreboard__player--active' : ''
                    }`}
                >
                    {player2Config.avatarUrl ? (
                        <img
                            src={resolveMediaUrl(player2Config.avatarUrl)}
                            alt={player2Config.name}
                            className="buzzer-fan__scoreboard__avatar"
                            style={{ borderColor: player2Config.color }}
                        />
                    ) : (
                        <div
                            className="buzzer-fan__scoreboard__avatar buzzer-fan__scoreboard__avatar--placeholder"
                            style={{ backgroundColor: player2Config.color }}
                        >
                            <i className="bi bi-person-fill" />
                        </div>
                    )}
                    <div className="buzzer-fan__scoreboard__info" style={{ textAlign: 'right' }}>
                        <span className="buzzer-fan__scoreboard__name" style={{ color: player2Config.color }}>
                            {player2Config.name}
                        </span>
                        <span className="buzzer-fan__scoreboard__score">{player2Config.score ?? 0} pts</span>
                    </div>
                </div>
            </section>

            {/* Banner Dinámico del Turno Rolado Activo */}
            {activeTurnPlayer && !isFinishedState && (
                <div
                    className={`buzzer-fan__turn-banner ${
                        gameState.disabledOptionIds?.length > 0 ? 'buzzer-fan__turn-banner--rebound' : ''
                    }`}
                >
                    <div className="buzzer-fan__turn-banner__left">
                        <div
                            className="buzzer-fan__turn-banner__avatar-wrap"
                            style={{ borderColor: activePlayerPrimaryColor }}
                        >
                            {activeTurnPlayer.avatarUrl ? (
                                <img
                                    src={resolveMediaUrl(activeTurnPlayer.avatarUrl)}
                                    alt={activeTurnPlayer.name}
                                    className="buzzer-fan__turn-banner__avatar"
                                />
                            ) : (
                                <i className="bi bi-person-fill fs-4" style={{ color: activePlayerPrimaryColor }} />
                            )}
                        </div>
                        <div className="buzzer-fan__turn-banner__details">
                            <span className="buzzer-fan__turn-banner__badge">
                                {gameState.disabledOptionIds?.length > 0 ? '¡REBOTE ACTIVO!' : 'TURNO ASIGNADO'}
                            </span>
                            <h2 className="buzzer-fan__turn-banner__player-name">{activeTurnPlayer.name}</h2>
                        </div>
                    </div>

                    <span className="buzzer-fan__turn-banner__action-prompt">
                        {gameState.selectedOptionId ? '¡OPCIÓN ENVIADA!' : 'SELECCIONA EN PANTALLA'}
                    </span>
                </div>
            )}

            {/* ============================================================= */}
            {/* CONTENIDO PRINCIPAL: PREGUNTA Y BOTONES DE OPCIONES           */}
            {/* ============================================================= */}
            <main className="buzzer-fan__main-content">
                {/* 1. Vista de Podio Final cuando la partida concluye */}
                {isFinishedState && (
                    <div className="buzzer-fan__podium">
                        <i className="bi bi-trophy-fill buzzer-fan__podium__trophy-icon" />
                        <h2 className="buzzer-fan__podium__title">
                            {podiumStandings?.isTie ? '¡EMPATE TÉCNICO!' : '¡CAMPEÓN DE LA BATALLA!'}
                        </h2>
                        <h3 className="buzzer-fan__podium__winner-name">
                            {podiumStandings?.winner || 'Ganador'}
                        </h3>

                        <div className="buzzer-fan__podium__scores-breakdown">
                            <div
                                className={`buzzer-fan__podium__score-box ${
                                    player1Config.score >= player2Config.score ? 'buzzer-fan__podium__score-box--winner' : ''
                                }`}
                            >
                                <span className="buzzer-fan__podium__score-box__player-name" style={{ color: player1Config.color }}>
                                    {player1Config.name}
                                </span>
                                <span className="buzzer-fan__podium__score-box__points">
                                    {player1Config.score ?? 0} PTS
                                </span>
                            </div>

                            <div
                                className={`buzzer-fan__podium__score-box ${
                                    player2Config.score >= player1Config.score ? 'buzzer-fan__podium__score-box--winner' : ''
                                }`}
                            >
                                <span className="buzzer-fan__podium__score-box__player-name" style={{ color: player2Config.color }}>
                                    {player2Config.name}
                                </span>
                                <span className="buzzer-fan__podium__score-box__points">
                                    {player2Config.score ?? 0} PTS
                                </span>
                            </div>
                        </div>

                        <span className="buzzer-fan__podium__status-hint">
                            Esperando inicio de una nueva partida...
                        </span>
                    </div>
                )}

                {/* 2. Vista de Pregunta y Opciones Táctiles Roladas */}
                {isQuestionActive && (
                    <>
                        {/* Tarjeta con Instrucción de la Pregunta */}
                        <div className="buzzer-fan__question-card">
                            <div className="buzzer-fan__question-card__header-meta">
                                <span className="buzzer-fan__question-card__number-badge">
                                    PREGUNTA {currentQuestionNumber} / {totalQuestionsCount}
                                </span>
                                {gameState.currentQuestion?.category && (
                                    <span className="buzzer-fan__question-card__category-badge">
                                        {gameState.currentQuestion.category}
                                    </span>
                                )}
                            </div>

                            <h3 className="buzzer-fan__question-card__instruction">
                                {gameState.currentQuestion?.instruction || 'Cargando pregunta...'}
                            </h3>

                            {gameState.currentQuestion?.imageUrl && (
                                <img
                                    src={resolveMediaUrl(gameState.currentQuestion.imageUrl)}
                                    alt="Imagen de referencia"
                                    className="buzzer-fan__question-card__image-preview"
                                />
                            )}
                        </div>

                        {/* Grilla de Botones de Opciones Touch (>60px) */}
                        <div className="buzzer-fan__options-grid">
                            {gameState.currentQuestion?.options?.map((optionItem, optionIndex) => {
                                const optionLetter = OPTION_LETTERS[optionIndex] || `${optionIndex + 1}`;
                                const isSelected = gameState.selectedOptionId === optionItem.id;
                                const isDisabledByRebound = gameState.disabledOptionIds?.includes(optionItem.id);
                                const isRevealed = gameState.isAnswerRevealed;
                                const isCorrect = isRevealed && (optionItem.isCorrect || (isSelected && gameState.isCorrect === true));
                                const isStrike = isRevealed && isSelected && gameState.isCorrect === false;
                                const isUnlockedForTap = Boolean(activeTurnPlayer) && !isRevealed && !isDisabledByRebound;

                                let buttonModifierClass = '';
                                if (isCorrect) {
                                    buttonModifierClass = 'buzzer-fan__option-btn--correct';
                                } else if (isStrike) {
                                    buttonModifierClass = 'buzzer-fan__option-btn--strike';
                                } else if (isSelected) {
                                    buttonModifierClass = 'buzzer-fan__option-btn--selected';
                                } else if (isDisabledByRebound) {
                                    buttonModifierClass = 'buzzer-fan__option-btn--disabled buzzer-fan__option-btn--struck';
                                } else if (isUnlockedForTap) {
                                    buttonModifierClass = 'buzzer-fan__option-btn--unlocked';
                                } else {
                                    buttonModifierClass = 'buzzer-fan__option-btn--locked';
                                }

                                return (
                                    <button
                                        key={optionItem.id || `opt-${optionIndex}`}
                                        type="button"
                                        className={`buzzer-fan__option-btn ${buttonModifierClass}`}
                                        disabled={!isUnlockedForTap && !isSelected}
                                        onClick={() => handleSelectOption(optionItem)}
                                        aria-label={`Opción ${optionLetter}: ${optionItem.text}`}
                                    >
                                        <div className="buzzer-fan__option-btn__badge">
                                            {optionLetter}
                                        </div>

                                        <span className="buzzer-fan__option-btn__text">
                                            {optionItem.text}
                                        </span>

                                        {isSelected && !isRevealed && (
                                            <i className="bi bi-fingerprint buzzer-fan__option-btn__status-icon text-warning" />
                                        )}

                                        {isCorrect && (
                                            <i className="bi bi-check-circle-fill buzzer-fan__option-btn__status-icon text-success" />
                                        )}

                                        {isStrike && (
                                            <i className="bi bi-x-circle-fill buzzer-fan__option-btn__status-icon text-danger" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Tarjeta de Alerta de Veredicto (Correcto / Strike) */}
                        {gameState.isAnswerRevealed && (
                            <div
                                className={`buzzer-fan__verdict-card ${
                                    gameState.isCorrect
                                        ? 'buzzer-fan__verdict-card--correct'
                                        : 'buzzer-fan__verdict-card--incorrect'
                                }`}
                            >
                                <i
                                    className={`buzzer-fan__verdict-card__icon ${
                                        gameState.isCorrect
                                            ? 'bi bi-check-circle-fill text-success'
                                            : 'bi bi-exclamation-octagon-fill text-danger'
                                    }`}
                                />
                                <h3
                                    className={`buzzer-fan__verdict-card__title ${
                                        gameState.isCorrect
                                            ? 'buzzer-fan__verdict-card__title--correct'
                                            : 'buzzer-fan__verdict-card__title--incorrect'
                                    }`}
                                >
                                    {gameState.isCorrect ? '¡RESPUESTA CORRECTA! (+1 PTO)' : '¡RESPUESTA INCORRECTA'}
                                </h3>
                                <span className="buzzer-fan__verdict-card__subtitle">
                                    {gameState.isCorrect
                                        ? '¡Punto registrado en el marcador del estadio!'
                                        : 'Esperando si se otorga el turno al contrincante...'}
                                </span>
                            </div>
                        )}
                    </>
                )}

                {/* 3. Vista de Espera / Standby cuando no hay pregunta activa */}
                {!isQuestionActive && !isFinishedState && (
                    <div className="buzzer-fan__waiting-view">
                        <div className="buzzer-fan__waiting-view__icon-ring">
                            <i className="bi bi-broadcast buzzer-fan__waiting-view__buzzer-icon" />
                        </div>
                        <h2 className="buzzer-fan__waiting-view__title">
                            {gameState.state === 'cover'
                                ? '¡PREPÁRENSE PARA EL COMBATE!'
                                : gameState.state === 'game_ready'
                                    ? 'LA BATALLA ESTÁ POR COMENZAR'
                                    : 'ESPERANDO PREGUNTA...'}
                        </h2>
                        <p className="buzzer-fan__waiting-view__subtitle">
                            Mantén la vista en la pantalla del estadio. Cuando aparezca la pregunta, ¡pulsa tu botón para tomar el turno!
                        </p>
                    </div>
                )}
            </main>

            {/* Pie de Página */}
            <footer className="buzzer-fan__footer">
                Hecho con ♥ por Interaktia | 2026
            </footer>
        </div>
    );
};

export default BuzzerBattleFan;
